import { WidgetType } from "@custom-types/Types";
import { useMemo } from "react";
import _ from "lodash";
import { WidgetRuleItem } from "@components/widgets/base/setup/widget-rules/Interfaces";
import { useRuleData } from "@hooks/widget-rules/useRuleData";
import { useQuery } from "@tanstack/react-query";
import { AI_TYPE } from "@resources/Constants";
import { ObjectType } from "src/pages/analytics/menu/btns/ObjectTypeButton";

export const RULE_CACHE_TIME_MS = 1000 * 60 * 60;

interface AnalyticsRuleDataParams {
  widgetType?: WidgetType;
  inValidateCacheOnUnmount?: boolean;
  ruleCategory?: ObjectType;
  aiType?: string;
}

export const useAnalyticsRuleData = (
  param: AnalyticsRuleDataParams,
  queryOptions?: Parameters<typeof useQuery>[2]
) => {
  const { widgetType } = param;
  const { data, isLoading } = useRuleData(
    {
      widgetType,
      inValidateCacheOnUnmount: param.inValidateCacheOnUnmount
    },
    {
      staleTime: RULE_CACHE_TIME_MS,
      cacheTime: RULE_CACHE_TIME_MS,
      ...queryOptions
    }
  );

  // filter by rule type ( object type)
  const filtered = useMemo(() => {
    if (!param.ruleCategory) {
      return data;
    }

    if (param.aiType) {
      return data
        .map((v) => {
          return {
            ...v,
            children: v.children?.filter((child) => {
              const ruleType = String(child.value).split(
                ":"
              )[0] as keyof typeof AI_TYPE;

              return (() => {
                return ruleType === (param.aiType as keyof typeof AI_TYPE);
              })();
            })
          };
        })
        .filter((v) => !_.isEmpty(v.children));
    }

    return data
      .map((v) => {
        return {
          ...v,
          children: v.children?.filter((child) => {
            const ruleType = String(child.value).split(
              ":"
            )[0] as keyof typeof AI_TYPE;

            return (() => {
              switch (param.ruleCategory) {
                case "People":
                  return (
                    ruleType === AI_TYPE.PEOPLE_COUNT ||
                    ruleType === AI_TYPE.QUEUE_MANAGEMENT ||
                    ruleType === AI_TYPE.SLIP_AND_FALL ||
                    ruleType === AI_TYPE.CROWD_COUNT
                  );
                case "Cart":
                  return (
                    ruleType === AI_TYPE.SHOPPING_CART_COUNT ||
                    ruleType === AI_TYPE.SHOPPING_CART_QUEUE_MANAGEMENT
                  );
                case "Vehicle":
                  return (
                    ruleType === AI_TYPE.VEHICLE_COUNT ||
                    ruleType === AI_TYPE.VEHICLE_QUEUE_MANAGEMENT
                    // ruleType === AI_TYPE.STOPPED_VEHICLE ||
                    // ruleType === AI_TYPE.TRAFFIC_JAM
                  );
                default:
                  return true;
              }
            })();
          })
        };
      })
      .filter((v) => !_.isEmpty(v.children));
  }, [data, param.ruleCategory]);

  // re-groupBy location and area
  const converted = useMemo(() => {
    if (filtered.length > 0) {
      const locationData = _.map(filtered as WidgetRuleItem[], (v) => ({
        ...v,
        location: v.metaData?.location,
        area: v.metaData?.area
      }));

      const groupByLocation = _.groupBy(locationData, (v) => v.location);
      const groupByLocationAndArea = _.mapValues(groupByLocation, (items) =>
        _.groupBy(items, (v) => v.area)
      );

      return [
        {
          title: "All",
          label: "All",
          value: "All",
          children: Object.keys(groupByLocationAndArea).map((locationKey) => {
            const children = Object.keys(
              groupByLocationAndArea[locationKey]
            ).map((areaKey) => {
              return {
                title: areaKey,
                label: areaKey,
                value: `${locationKey}_${areaKey}`,
                children: groupByLocationAndArea[locationKey][areaKey],
                metaData: {
                  name: areaKey
                }
              };
            });

            return {
              title: locationKey,
              label: locationKey,
              value: locationKey,
              children,
              metaData: {
                name: locationKey
              }
            };
          })
        }
      ];
    }

    return [];
  }, [filtered]);

  return {
    data: converted,
    originData: data,
    isLoading
  };
};
